// Loads paperdoll layer PNGs on demand, composites requested (anim, dir, frame)
// views into a cached offscreen canvas, and notifies subscribers when new
// images finish loading so pending Tokens can re-render.

import { LAYERS, FRAME_SIZE, FRAMES_PER_ANIM, DIRS, layerUrl } from './paperdolls';

const imageCache = new Map(); // url -> { img, ready } ; ready true once onload fires
const frameCache = new Map(); // frameKey -> HTMLCanvasElement
const FRAME_CACHE_CAP = 256;
const listeners = new Set();

function notify() { listeners.forEach((cb) => cb()); }

function getImage(url) {
  let entry = imageCache.get(url);
  if (entry) return entry;
  const img = new Image();
  entry = { img, ready: false };
  imageCache.set(url, entry);
  img.onload  = () => { entry.ready = true; notify(); };
  img.onerror = () => { entry.ready = false; entry.errored = true; };
  img.src = url;
  return entry;
}

function configKey(config) {
  const ls = config.layers || {};
  // Serialize tints deterministically (sorted) so cache keys are stable.
  const tints = config.tints || (config.hairColor ? { hair: config.hairColor } : {});
  const tintStr = Object.keys(tints).sort().map((k) => tints[k] ? `${k}:${tints[k]}` : '').filter(Boolean).join(',');
  const ws = config.widthScale  ?? 1;
  const hs = config.heightScale ?? 1;
  return `${config.gender}|${LAYERS.map((l) => `${l.slot}:${ls[l.slot] || ''}`).join(',')}|t:${tintStr}|s:${ws},${hs}`;
}

function frameKey(config, anim, dir, frame, size) {
  return `${configKey(config)}|${anim}|${dir}|${frame}|${size}`;
}

/** Returns resolved { url, ready } list for this config + anim (active layers only). */
function resolveLayers(config, anim) {
  const gender = config.gender || 'M';
  const ls     = config.layers || {};
  const list   = [];
  for (const layer of LAYERS) {
    const variant = ls[layer.slot];
    if (!variant) continue;
    if (!layer.anims.includes(anim)) continue;
    const url = layerUrl(layer, gender, variant, anim);
    const { img, ready, errored } = getImage(url);
    if (errored) continue;
    list.push({ img, ready, url, layer });
  }
  return list;
}

/**
 * Returns a composited HTMLCanvasElement for (config, anim, dir, frame) at the
 * requested pixel size. Returns null if any required layer isn't loaded yet;
 * subscribers fire once the missing images complete so callers re-render.
 */
export function getPaperdollFrame(config, anim, dir, frame, size) {
  if (!config || !config.layers) return null;
  if (typeof document === 'undefined') return null;

  const key = frameKey(config, anim, dir, frame, size);
  const hit = frameCache.get(key);
  if (hit) {
    // MRU touch
    frameCache.delete(key);
    frameCache.set(key, hit);
    return hit;
  }

  const layers = resolveLayers(config, anim);
  if (layers.length === 0) return null;
  if (layers.some((l) => !l.ready)) return null;

  const canvas = document.createElement('canvas');
  canvas.width  = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const row = DIRS.indexOf(dir);
  const frameCount = FRAMES_PER_ANIM[anim] || 4;
  const f = Math.max(0, Math.min(frameCount - 1, frame | 0));
  const sx = f * FRAME_SIZE;
  const sy = (row < 0 ? 0 : row) * FRAME_SIZE;

  // Migrate legacy hairColor → tints.hair so old saved configs still work.
  const tints = config.tints || (config.hairColor ? { hair: config.hairColor } : {});
  const widthScale  = config.widthScale  ?? 1;
  const heightScale = config.heightScale ?? 1;
  const drawW = size * widthScale;
  const drawH = size * heightScale;
  // Admurin characters sit in the upper-middle of the 128 cell with lots of
  // pad beneath (cell has room for trailing weapons / tall props). Feet land
  // at roughly y=85. Anchor scaling here so height changes don't drift feet.
  const FEET_RATIO = 85 / 128;
  const dx = (size - drawW) / 2;                      // horizontally centred
  const dy = (size - drawH) * FEET_RATIO;             // feet-anchored

  for (const { img, layer } of layers) {
    const tint = tints[layer.slot];
    if (tint) {
      // Tint path: pre-brighten so pale colours actually read as themselves,
      // then flat-fill the target colour via source-atop at reduced alpha so
      // some of the original shading bleeds through.
      const off = document.createElement('canvas');
      off.width = size; off.height = size;
      const octx = off.getContext('2d');
      octx.imageSmoothingEnabled = false;
      octx.filter = 'brightness(1.6) saturate(0.4)';
      octx.drawImage(img, sx, sy, FRAME_SIZE, FRAME_SIZE, dx, dy, drawW, drawH);
      octx.filter = 'none';
      octx.globalCompositeOperation = 'source-atop';
      octx.fillStyle = tint;
      octx.globalAlpha = 0.8;
      octx.fillRect(dx, dy, drawW, drawH);
      octx.globalAlpha = 1;
      octx.globalCompositeOperation = 'source-over';
      ctx.drawImage(off, 0, 0);
    } else {
      ctx.drawImage(img, sx, sy, FRAME_SIZE, FRAME_SIZE, dx, dy, drawW, drawH);
    }
  }

  if (frameCache.size >= FRAME_CACHE_CAP) {
    const oldest = frameCache.keys().next().value;
    if (oldest !== undefined) frameCache.delete(oldest);
  }
  frameCache.set(key, canvas);
  return canvas;
}

export function preloadPaperdoll(config) {
  if (!config || !config.layers) return;
  // Prime images for Idle + Move (most common anims at map load).
  resolveLayers(config, 'Idl');
  resolveLayers(config, 'Mov');
}

export function subscribe(cb) { listeners.add(cb); return () => listeners.delete(cb); }
